/**
 * React hook for offline functionality
 */

import { useEffect, useState, useCallback } from 'react';
import { syncService } from '../services/syncService';
import { offlineStorage } from '../services/offlineStorage';
import type { SyncProgress } from '../services/syncService';
import type { SyncQueueItem } from '../services/offlineStorage';

export interface UseOfflineResult {
  isOnline: boolean;
  syncProgress: SyncProgress | null;
  syncStats: {
    pending: number;
    failed: number;
    lastSync: number | null;
  };
  /** The refused submissions, with the server's reason (LOG-1023-05). */
  failedItems: SyncQueueItem[];
  syncNow: () => Promise<void>;
  clearFailedItems: () => Promise<void>;
  /** Drop one refused submission (Chris 10/25: delete them one at a time). */
  discardFailedItem: (id: string) => Promise<void>;
}

export function useOffline(): UseOfflineResult {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [syncProgress, setSyncProgress] = useState<SyncProgress | null>(null);
  const [syncStats, setSyncStats] = useState({
    pending: 0,
    failed: 0,
    lastSync: null as number | null,
  });
  const [failedItems, setFailedItems] = useState<SyncQueueItem[]>([]);

  useEffect(() => {
    // Subscribe to online status
    const unsubscribeOnline = syncService.onOnlineStatusChange(setIsOnline);

    // Subscribe to sync progress
    const unsubscribeProgress = syncService.onSyncProgress(setSyncProgress);

    // Update sync stats periodically
    const updateStats = async () => {
      const stats = await syncService.getSyncStats();
      setSyncStats(stats);
      setFailedItems(
        stats.failed > 0 ? await offlineStorage.getSyncQueue('failed') : []
      );
    };

    updateStats();
    const interval = setInterval(updateStats, 5000);

    // Initialize
    setIsOnline(syncService.isOnline());

    // Start background sync if online
    if (syncService.isOnline()) {
      syncService.startBackgroundSync();
    }

    return () => {
      unsubscribeOnline();
      unsubscribeProgress();
      clearInterval(interval);
    };
  }, []);

  const syncNow = useCallback(async () => {
    if (isOnline) {
      // Retry now: the one path that replays items the server already refused.
      await syncService.syncAll({ includeFailed: true });
    }
  }, [isOnline]);

  const clearFailedItems = useCallback(async () => {
    const failedItems = await offlineStorage.getSyncQueue('failed');
    for (const item of failedItems) {
      await offlineStorage.removeSyncQueueItem(item.id);
    }

    // Update stats
    const stats = await syncService.getSyncStats();
    setSyncStats(stats);
    setFailedItems([]);
  }, []);

  const discardFailedItem = useCallback(async (id: string) => {
    await offlineStorage.removeSyncQueueItem(id);
    const stats = await syncService.getSyncStats();
    setSyncStats(stats);
    setFailedItems(
      stats.failed > 0 ? await offlineStorage.getSyncQueue('failed') : []
    );
  }, []);

  return {
    isOnline,
    syncProgress,
    syncStats,
    failedItems,
    syncNow,
    clearFailedItems,
    discardFailedItem,
  };
}

// Hook for offline-first data fetching
export function useOfflineData<T>(
  key: string,
  fetcher: () => Promise<T>,
  options?: {
    refreshInterval?: number;
    cacheTime?: number;
  }
): {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
  isOffline: boolean;
  refresh: () => Promise<void>;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { isOnline } = useOffline();

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      if (isOnline) {
        // Try to fetch fresh data
        const freshData = await fetcher();
        setData(freshData);

        // Cache the data
        localStorage.setItem(
          `offline_cache_${key}`,
          JSON.stringify({
            data: freshData,
            timestamp: Date.now(),
          })
        );
      } else {
        // Get from cache when offline
        const cached = localStorage.getItem(`offline_cache_${key}`);
        if (cached) {
          const { data: cachedData } = JSON.parse(cached);
          setData(cachedData);
        } else {
          throw new Error('No cached data available');
        }
      }
    } catch (err) {
      setError(err as Error);

      // Try to use cached data as fallback
      const cached = localStorage.getItem(`offline_cache_${key}`);
      if (cached) {
        const { data: cachedData } = JSON.parse(cached);
        setData(cachedData);
      }
    } finally {
      setIsLoading(false);
    }
  }, [key, fetcher, isOnline]);

  useEffect(() => {
    fetchData();

    // Set up refresh interval if specified
    if (options?.refreshInterval && isOnline) {
      const interval = setInterval(fetchData, options.refreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, options?.refreshInterval, isOnline]);

  return {
    data,
    error,
    isLoading,
    isOffline: !isOnline,
    refresh: fetchData,
  };
}

// Hook for managing offline forms
export function useOfflineForm<T extends Record<string, any>>(
  formKey: string
): {
  savedData: T | null;
  saveData: (data: T) => void;
  clearData: () => void;
  lastSaved: Date | null;
} {
  const [savedData, setSavedData] = useState<T | null>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  useEffect(() => {
    // Load saved form data
    const saved = localStorage.getItem(`offline_form_${formKey}`);
    if (saved) {
      const { data, timestamp } = JSON.parse(saved);
      setSavedData(data);
      setLastSaved(new Date(timestamp));
    }
  }, [formKey]);

  const saveData = useCallback(
    (data: T) => {
      const timestamp = Date.now();
      localStorage.setItem(
        `offline_form_${formKey}`,
        JSON.stringify({ data, timestamp })
      );
      setSavedData(data);
      setLastSaved(new Date(timestamp));
    },
    [formKey]
  );

  const clearData = useCallback(() => {
    localStorage.removeItem(`offline_form_${formKey}`);
    setSavedData(null);
    setLastSaved(null);
  }, [formKey]);

  return {
    savedData,
    saveData,
    clearData,
    lastSaved,
  };
}
